import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import _get from 'lodash/get';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import handleKeyPress from '../../../common/handleKeyPress';
import { APPLICATION_MODES, DRAFT_ACCESS } from '../../../constants/itemTypes/itemTypes';
import CustomTooltip from '../../../componentLibrary/CustomTooltip';
import rules from '../../../constants/businessRules/businessRules';
import * as Styled from './styles';

const contentSizeThreshold = rules.GMAIL_MAX_CONTENT_SIZE * 0.9;

const TopNavCenterTabs = ({
    setApplicationMode,
    applicationMode,
    isCanvasEmpty,
    htmlSize,
    hasDesignAccess,
    hasPreviewAccess,
}) => {
    const [previewLabel, setPreviewLabel] = useState('');
    const showPreviewIcon = isCanvasEmpty ? false : !!(htmlSize > contentSizeThreshold);
    const tabs = [
        {
            name: 'Design',
            mode: 'EDITOR',
            icon: '',
            showIcon: false,
            iconColor: '',
            label: hasDesignAccess ? '' : 'You need permission to edit this draft.',
            enabled: hasDesignAccess,
        },
        {
            name: 'Preview',
            mode: 'PREVIEW',
            icon: 'exclamation-triangle',
            showIcon: showPreviewIcon,
            iconColor: '#FF8F00',
            label: previewLabel,
            enabled: hasPreviewAccess && !isCanvasEmpty,
        },
        // { name: 'Commenting', mode: 'COMMENTING' }, TODO: Uncomment for future commenting feature
    ];

    useEffect(() => {
        if (!hasPreviewAccess) {
            setPreviewLabel(
                'Preview designs on desktop and mobile screens. Unlock this feature by requesting a full product demo.'
            );
        } else if (isCanvasEmpty) {
            setPreviewLabel('Your canvas is empty. Start designing your email to enable this tab.');
        } else {
            setPreviewLabel(showPreviewIcon ? "Check your draft's file size in the Preview Mode." : '');
        }
    }, [hasPreviewAccess, isCanvasEmpty, showPreviewIcon, setPreviewLabel]);

    return (
        <Styled.CenterNavUl>
            {tabs.map((tab) => (
                <Styled.TabLi
                    enabled={tab.enabled}
                    applicationMode={applicationMode}
                    tab={tab.mode}
                    label={tab.label}
                    key={tab.name}
                    data-tip
                    data-for={tab.name}
                    cursor={tab.enabled ? 'pointer' : 'not-allowed'}
                    // TODO: PIK-2203 Replace drilled prop with dispatch
                    onClick={tab.enabled ? setApplicationMode(APPLICATION_MODES[tab.mode]) : null}
                    onKeyDown={(e) =>
                        tab.enabled ? handleKeyPress(e, setApplicationMode(APPLICATION_MODES[tab.mode])) : null
                    }
                    tabIndex="0"
                    role="button"
                    aria-label={`Enter ${tab.mode} mode`}
                >
                    <Styled.TabNameLink applicationMode={applicationMode} tab={tab.mode}>
                        {tab.name}
                    </Styled.TabNameLink>
                    {tab.showIcon && (
                        <FontAwesomeIcon
                            style={{ fontSize: 14, color: tab.iconColor }}
                            icon={tab.icon}
                            label={tab.label}
                            data-tip
                            data-for={tab.label}
                        />
                    )}
                    {tab.label && (
                        <CustomTooltip
                            dataRef={tab.name}
                            width={154}
                            offset={{ top: 16 }}
                            text={tab.label}
                            showIcon={!hasPreviewAccess}
                        />
                    )}
                </Styled.TabLi>
            ))}
        </Styled.CenterNavUl>
    );
};
const mapStateToProps = (state) => {
    const access = _get(state, 'session.collaboration.access', DRAFT_ACCESS.OWNER);
    const hasDesignAccess = access !== DRAFT_ACCESS.READ;
    return {
        htmlSize: _get(state, 'htmlOutput.size', 0),
        hasDesignAccess,
        hasPreviewAccess: _get(state, 'session.features.F_PREVIEW', false),
    };
};

TopNavCenterTabs.propTypes = {
    setApplicationMode: PropTypes.func.isRequired,
    applicationMode: PropTypes.string.isRequired,
    isCanvasEmpty: PropTypes.bool.isRequired,
    htmlSize: PropTypes.number.isRequired,
    hasDesignAccess: PropTypes.bool.isRequired,
    hasPreviewAccess: PropTypes.bool.isRequired,
};

export default connect(mapStateToProps)(TopNavCenterTabs);
